import { type DependencyList, useCallback, useEffect, useRef, useState } from 'react'

export const useLatestRequestEffect = <T>(
    load: () => Promise<T> | undefined,
    onSuccess: (value: T) => void,
    dependencies: DependencyList,
    onError: (error: unknown) => void = (error) => console.error(error),
    onClear: () => void = () => {},
): (() => void) => {
    const latestRequest = useRef(0)
    const [refreshNonce, setRefreshNonce] = useState(0)
    const refresh = useCallback(() => setRefreshNonce((value) => value + 1), [])

    useEffect(() => {
        const request = ++latestRequest.current
        let promise: Promise<T | undefined>

        try {
            promise = Promise.resolve(load())
        } catch (error) {
            promise = Promise.reject(error)
        }

        promise
            .then((value) => {
                if (request === latestRequest.current) {
                    if (value === undefined) {
                        onClear()
                    } else {
                        onSuccess(value)
                    }
                }
            })
            .catch((error) => {
                if (request === latestRequest.current) {
                    onClear()
                    onError(error)
                }
            })

        return () => {
            if (request === latestRequest.current) {
                latestRequest.current++
            }
        }
    }, [...dependencies, refreshNonce])

    return refresh
}
